import {markRaw} from 'vue';
import type {Raw, Component} from 'vue';
import type {SharedMap} from '@/lib/classes/SharedMap';
import type {AddableFeature} from '@/store/baseIndex';
import type Fuse from 'fuse.js';

import {Overlay} from './Overlay.js';
import {Feature} from './Feature.js';
import {OverlayService} from './OverlaysService.js';

import AccessRouteIcon from '@/components/svgs/AccessRouteIcon.vue';
import PolygonIcon from '@/components/svgs/PolygonIcon.vue';
import PolylineIcon from '@/components/svgs/PolylineIcon.vue';
import AddPropertyIcon from '@/components/svgs/MenuIcons/AddPropertyIcon.vue';
import LabelIcon from '@/components/svgs/LabelIcon.vue';
import FolderIcon from '@/components/svgs/FolderIcon.vue';
import ImageIcon from '@/components/svgs/ImageIcon.vue';
import BlockPlanIcon from '@/components/svgs/ToolboxIcons/BlockPlanIcon.vue';
import FileUploadIcon from '@/components/svgs/FileUploadIcon.vue';

import {createNewAccessRoute} from '@/services/GraphicService/AccessRouteGraphic/createAccessRouteGraphic.js';
import {createNewLabel} from '@/services/GraphicService/LabelGraphic/createLabelGraphic.js';
import {createNewPlaceMarker} from '@/services/GraphicService/PlaceMarkerGraphic/createPlaceMarkerGraphic.js';
import {createNewPoly} from '@/services/GraphicService/PolyGraphic/createNewPolyGraphic.js';
import {createNewBlockPlan} from '@/services/GraphicService/BlockPlanGraphic/createBlockPlanGraphic.js';
import {createNewImageBlockPlan} from '@/services/GraphicService/ImageGraphic/createImageGraphic.js';
import {handleAddMultipleParcels} from '@/services/GraphicService/VectorTileGraphic/customClickHandlers/addMultipleParcels';

type FuseFeatureResult = Fuse.FuseResult<Feature>;

interface FeatureOptionIcon {
  /** the name for the icon */
  name: string;
  /** the raw component for the icon */
  raw: Raw<Component>;
  /** the height, needed for marker icons */
  height?: string;
}

export type FeatureOption = {
  /** a wrapper function that creates a new feature and adds it to the given parent */
  createNewFeature?: (parent: Overlay | SharedMap, finishedCallback: () => void) => unknown;
  /** whether or not this feature type is disabled at the current zoom level */
  disabled?: boolean;
  /** the label to be displayed in the dropdown for this feature type */
  dropdownLabel: string;
  /** a function that creates a label for the search results */
  makeLabel?: (result: FuseFeatureResult) => [string, string];
  /** icon for feature */
  icon?: FeatureOptionIcon;
  /** icon for adding feature */
  addFeatureIcon?: FeatureOptionIcon;
  /** the maximum zoom level a user can create this feature at */
  maxCreateZoom: number;
  /** the minimum zoom level a user can create this feature at */
  minCreateZoom: number;
  /** the default name for this feature type - required for leaf features */
  defaultName: string;
  /** the pluralized name for this feature type - required for leaf features */
  pluralizedName?: string;
  /** the short name for compact UI like tabs; presentation falls back to a generic 'Feature' label when unset */
  shortName?: string;
  /** the InfoWindow tab-icon key resolved by MapService/Icons.js (e.g. 'Polygon-Icon') */
  tabIconName?: string;
  /** the options for the group */
  options?: Map<string, FeatureOption>;
};


/**
 * This function wraps the feature creation function with some extra context
 * @param parent either an overlay or a shared map
 * @param finishedCallback a function to be called when the feature is finished creating
 * @param createFn the function that creates the feature
 * @return A callback to be used when a user cancels creating a feature
 */
const wrapCreate = async (
  parent: Overlay | SharedMap,
  finishedCallback: () => void,
  createFn: (fn: (arg: object) => Promise<void>) => unknown,
): Promise<unknown> => {
  const finishedCreating = async (data: object) => {
    // When finished creating, this function likely sets the cancel callback to null in overlay edit submenu
    finishedCallback();
    // Add the new feature to the overlay
    const feature = await parent.newFeature(data);
    if (feature.featureType !== 'label') {
      feature.openInfoWindow({payload: null});
    }

    // You can only open an info window in edit mode if the placemarker exists as a feature
    if (feature.featureType === 'placeMarker') {
      const graphic = await feature.graphic;
      await graphic.setEditing(true, {openInEditMode: true});
    }
  };
  // Call the create function with the finished callback.
  // This returns a cancelCallback.
  return await createFn(finishedCreating);
};

const featureOptions = new Map<string, FeatureOption>();
featureOptions.set('png', {
  // creating new pngs right now is handled in AddFeatureButton.vue.
  // The reason for this is because that is where the kmz upload input exists.
  defaultName: 'Image',
  pluralizedName: 'Images',
  shortName: 'Image',
  tabIconName: 'Image-Icon',
  dropdownLabel: 'File Upload',
  // Display Image icon in Feature list
  icon: {
    name: 'ImageIcon',
    raw: markRaw(ImageIcon),
    height: '22px',
  },
  // Display File icon in dropdown for adding a Feature
  addFeatureIcon: {
    name: 'FileUploadIcon',
    raw: markRaw(FileUploadIcon),
    height: '22px',
  },
  makeLabel: (result) => [result.item.name, 'Image'],
  maxCreateZoom: 21,
  minCreateZoom: 0,
});
featureOptions.set('blockPlan', {
  defaultName: 'Block Plan',
  dropdownLabel: 'Block Plan',
  minCreateZoom: 13,
  maxCreateZoom: 21,
  icon: {
    name: 'BlockPlan',
    raw: markRaw(BlockPlanIcon),
  },
  options: new Map([
    ['blockPlan-industrial', {
      defaultName: 'Industrial Block Plan',
      pluralizedName: 'Industrial Block Plans',
      dropdownLabel: 'Industrial',
      minCreateZoom: 13,
      maxCreateZoom: 21,
      icon: {
        name: 'BlockPlan',
        raw: markRaw(BlockPlanIcon),
      },
      options: new Map([['blockPlan-crossDock', {
        createNewFeature: (parent, finishedCallback) =>
          wrapCreate(parent, finishedCallback, (callback) => createNewBlockPlan('crossDock', callback)),
        defaultName: 'Cross Dock',
        shortName: 'Cross Dock',
        tabIconName: 'BlockPlan-Icon',
        dropdownLabel: 'Cross Dock',
        minCreateZoom: 13,
        maxCreateZoom: 21,
        icon: {
          name: 'BlockPlan',
          raw: markRaw(BlockPlanIcon),
        },
        makeLabel: (result) => [result.item.name, 'Cross Dock'],
      }], ['blockPlan-singleLoad', {
        createNewFeature: (parent, finishedCallback) =>
          wrapCreate(parent, finishedCallback, (callback) => createNewBlockPlan('singleLoad', callback)),
        defaultName: 'Single Load',
        shortName: 'Single Load',
        tabIconName: 'BlockPlan-Icon',
        dropdownLabel: 'Single Load',
        minCreateZoom: 13,
        maxCreateZoom: 21,
        icon: {
          name: 'BlockPlan',
          raw: markRaw(BlockPlanIcon),
        },
        makeLabel: (result) => [result.item.name, 'Single Load'],
      }]]),
    }],
    ['blockPlan-dataCenter', {
      defaultName: 'Data Center Block Plan',
      dropdownLabel: 'Data Center',
      minCreateZoom: 13,
      maxCreateZoom: 21,
      icon: {
        name: 'BlockPlan',
        raw: markRaw(BlockPlanIcon),
      },
      options: new Map([['blockPlan-dataCenter-8pod', {
        createNewFeature: (parent, finishedCallback) =>
          wrapCreate(parent, finishedCallback, (callback) => createNewImageBlockPlan('8pod', callback)),
        defaultName: '8 Pod (48MW)',
        dropdownLabel: '8 Pod (48MW)',
        minCreateZoom: 13,
        maxCreateZoom: 21,
        icon: {
          name: 'BlockPlan',
          raw: markRaw(BlockPlanIcon),
        },
        makeLabel: (result) => [result.item.name, '8 Pod (48MW)'],
      }], ['blockPlan-dataCenter-12pod', {
        createNewFeature: (parent, finishedCallback) =>
          wrapCreate(parent, finishedCallback, (callback) => createNewImageBlockPlan('12pod', callback)),
        defaultName: '12 Pod (62MW)',
        dropdownLabel: '12 Pod (62MW)',
        minCreateZoom: 13,
        maxCreateZoom: 21,
        icon: {
          name: 'BlockPlan',
          raw: markRaw(BlockPlanIcon),
        },
        makeLabel: (result) => [result.item.name, '12 Pod (62MW)'],
      }]]),
    }],
    ['blockPlan-solar', {
      defaultName: 'Solar Block Plan',
      dropdownLabel: 'Solar',
      minCreateZoom: 13,
      maxCreateZoom: 21,
      icon: {
        name: 'BlockPlan',
        raw: markRaw(BlockPlanIcon),
      },
      options: new Map([['blockPlan-solar-rooftopArray', {
        createNewFeature: (parent, finishedCallback) =>
          wrapCreate(parent, finishedCallback, (callback) => createNewImageBlockPlan('rooftopArray', callback)),
        defaultName: 'Rooftop Array (500 kW)',
        dropdownLabel: 'Rooftop Array (500 kW)',
        minCreateZoom: 13,
        maxCreateZoom: 21,
        icon: {
          name: 'BlockPlan',
          raw: markRaw(BlockPlanIcon),
        },
        makeLabel: (result) => [result.item.name, 'Rooftop Array (500 kW)'],
      }]]),
    }],
  ]),
});
featureOptions.set('parcels', {
  createNewFeature: (parent, finishedCallback) => handleAddMultipleParcels(parent, finishedCallback),
  defaultName: 'Parcel',
  dropdownLabel: 'Multiple Parcels',
  icon: {
    name: 'Polygon',
    raw: markRaw(PolygonIcon),
  },
  makeLabel: (result) => [result.item.name, 'Parcel'],
  maxCreateZoom: 21,
  minCreateZoom: 14,
});
featureOptions.set('polygon', {
  // creating new polygons is also handled when clicking the 'Add to Overlay' button
  // in the ParcelInfoWindow.vue component.
  createNewFeature: (parent, finishedCallback) => wrapCreate(parent, finishedCallback, createNewPoly),
  defaultName: 'Polygon',
  pluralizedName: 'Polygons',
  shortName: 'Polygon',
  tabIconName: 'Polygon-Icon',
  dropdownLabel: 'Polygon/line',
  icon: {
    name: 'Polygon',
    raw: markRaw(PolygonIcon),
  },
  makeLabel: (result) => [result.item.name, 'Polygon'],
  maxCreateZoom: 21,
  minCreateZoom: 13,
});
featureOptions.set('polyline', {
  // Polylines are created from the polygon option, but they have some unique properties
  defaultName: 'Polyline',
  pluralizedName: 'Polylines',
  tabIconName: 'Polyline-Icon',
  dropdownLabel: 'Polyline',
  icon: {
    name: 'Polyline',
    raw: markRaw(PolylineIcon),
  },
  makeLabel: (result) => [result.item.name, 'Polyline'],
  maxCreateZoom: 21,
  minCreateZoom: 13,
});
featureOptions.set('route', {
  createNewFeature: (parent, finishedCallback) => wrapCreate(parent, finishedCallback, createNewAccessRoute),
  defaultName: 'Access Route',
  pluralizedName: 'Access Routes',
  shortName: 'Route',
  tabIconName: 'AccessRoute-Icon',
  dropdownLabel: 'Access Route',
  icon: {
    name: 'AccessRoute',
    raw: markRaw(AccessRouteIcon),
  },
  makeLabel: (result) => [result.item.name, 'Access Route'],
  maxCreateZoom: 21,
  minCreateZoom: 13,
});
featureOptions.set('label', {
  createNewFeature: (parent, finishedCallback) => wrapCreate(parent, finishedCallback, createNewLabel),
  defaultName: 'Label',
  pluralizedName: 'Labels',
  shortName: 'Label',
  tabIconName: 'Label-Icon',
  dropdownLabel: 'Label',
  icon: {
    name: 'Label',
    raw: markRaw(LabelIcon),
  },
  makeLabel: (result) => [result.item.name, 'Label'],
  maxCreateZoom: 21,
  minCreateZoom: 13,
});
featureOptions.set('placeMarker', {
  createNewFeature: (parent, finishedCallback) => wrapCreate(parent, finishedCallback, createNewPlaceMarker),
  defaultName: 'Place Marker',
  pluralizedName: 'Place Markers',
  dropdownLabel: 'Place Marker',
  icon: {
    name: 'AddProperty',
    raw: markRaw(AddPropertyIcon),
  },
  makeLabel: (result) => [result.item.name, 'Placemarker'],
  maxCreateZoom: 21,
  minCreateZoom: 0,
});
featureOptions.set('folder', {
  createNewFeature: (parent: Overlay | SharedMap) => parent.createFolder(),
  defaultName: 'Folder',
  shortName: 'Folder',
  dropdownLabel: 'Folder',
  icon: {
    name: 'Folder',
    raw: markRaw(FolderIcon),
  },
  maxCreateZoom: 21,
  minCreateZoom: 0,
});

/**
 * Using the featureOptions map variable, recalculates the addable features given the valid keys and current zoom level.
 * This isn't using the featureOptions available in the state because it is a constant and should not be modified.
 */
const getAddableFeatures = (addableFeatures: AddableFeature[], zoomLevel: number): Array<[string, FeatureOption]> => {
  const features = addableFeatures.map(({type, ignoreZoomLevel}) => {
    let feature = featureOptions.get(type);
    if (!feature) {
      // If the feature is not found at the top level, check the options of the other features
      for (const value of featureOptions.values()) {
        if ('options' in value && value.options && value.options.has(type)) {
          feature = value.options.get(type);
          break;
        }
      }
    }
    if (!feature) {
      console.error(`Feature ${type} does not exist in featureOptions`);
      return;
    }

    return [type, {
      disabled: !ignoreZoomLevel &&
        (zoomLevel < feature.minCreateZoom || zoomLevel > feature.maxCreateZoom),
      ...feature,
    }] as [string, FeatureOption];
  });
  return features.filter((feature): feature is [string, FeatureOption] => feature !== undefined);
};

/**
 * Recursively finds a feature option by its type key, searching nested option groups.
 * Returns null without logging when not found, so presentation lookups can fall back silently.
 */
const findFeatureOption = (featureType: string): FeatureOption | null => {
  const search = (options: Map<string, FeatureOption>): FeatureOption | null => {
    for (const [key, value] of options) {
      if (key === featureType) return value;
      if (value.options) {
        const found = search(value.options);
        if (found) return found;
      }
    }
    return null;
  };
  return search(featureOptions);
};

const getFeatureIcon = (featureType: string): FeatureOptionIcon | null => {
  const findIcon = (options: Map<string, FeatureOption>, featureType: string): FeatureOptionIcon | null => {
    for (const [key, value] of options) {
      if (key === featureType) return value.icon ?? null;
      if ('options' in value && value.options) {
        const icon = findIcon(value.options, featureType);
        if (icon) return icon;
      }
    }
    return null;
  };

  const icon = findIcon(featureOptions as Map<string, FeatureOption>, featureType);
  if (icon) return icon;
  console.error(`Feature ${featureType} does not exist in featureOptions`);
  return null;
};

export {
  Overlay,
  Feature,
  OverlayService,
  featureOptions,
  findFeatureOption,
  getAddableFeatures,
  getFeatureIcon,
};
